import React, { useState, useRef, useEffect, useMemo, useCallback } from "react"
import { Offcanvas, CloseButton, Button, Form, Modal, Tab, Nav, Card, Row, Col, Badge } from 'react-bootstrap'
import { getFirestore, collection, getDocs, doc, setDoc, updateDoc, getDoc, deleteDoc, query, where } from 'firebase/firestore'
import { FaCrown, FaUserTag, FaEnvelope, FaCalendarAlt, FaTrashAlt, FaCogs, FaUser, FaMapMarkerAlt, FaLock, FaHistory, FaUsers, FaTools, FaShieldAlt, FaPlus, FaCheckCircle, FaCheck, FaTimesCircle, FaFlag, FaArrowLeft, FaArrowRight, FaUserSlash } from 'react-icons/fa';
import QRCode from 'react-qr-code';
import Cropper from 'react-easy-crop';
import { toast } from 'react-toastify'
import { convertDate, spinner, makeid } from "../utils/Helper"
import ChangePasswordButton from "../entry/ChangePasswordButton";
import apc from '../images/user.png'
import MemberTransactionHistory from "./MemberTransactionHistory";
import ManualPayment from "./ManualPayment";

import './xDocList.css';

// --- CUSTOM HOOK: DEBOUNCE VALUE ---
function useDebounce(value, delay = 200) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
}

// --- COMPONENT: MEMBERSHIP MANAGER ---
const MembershipManager = ({ theme }) => {
    const db = getFirestore();
    const [levels, setLevels] = useState({});
    const [newLevel, setNewLevel] = useState({
        id: '', cost: '', occurrence: 'Annually', addRegistration: false, account_flag: false, description: '', lateFee: ''
    });

    const fetchLevels = useCallback(async () => {
        try {
            const snap = await getDocs(collection(db, "membership_levels"));
            const data = {};
            snap.forEach(doc => data[doc.id] = doc.data());
            setLevels(data);
        } catch (e) {
            toast.error("Failed loading rates");
        }
    }, [db]);

    useEffect(() => { fetchLevels(); }, [fetchLevels]);

    const handleSave = async (id, cost, occurrence, addRegistration, description, lateFee, account_flag) => {
        spinner(true);
        try {
            await setDoc(doc(db, "membership_levels", id), {
                cost: Number(cost),
                occurrence,
                addRegistration: Boolean(addRegistration),
                account_flag: Boolean(account_flag),
                description: String(description || ''),
                lateFee: Number(lateFee || 0)
            });
            toast.success("Level Updated");
            fetchLevels();
        } catch (e) { toast.error("Error saving level"); }
        finally { spinner(false); }
    };

    const handleDelete = async (id) => {
        if (window.confirm(`Delete ${id}?`)) {
            await deleteDoc(doc(db, "membership_levels", id));
            fetchLevels();
        }
    };

    return (
        <div className="p-3">
            <h5 className="mb-3 text-info d-flex align-items-center">
                <FaCogs className="me-2" /> Membership Rates
            </h5>

            <Card className="mb-4 border-info border-opacity-25 bg-info bg-opacity-10 text-white">
                <Card.Body className="p-3">
                    <h6 className="text-info small fw-bold mb-3"><FaPlus className="me-1" /> Add New Level</h6>
                    <Row className="g-2">
                        <Col xs={12}>
                            <label className="meta-label">Level Name</label>
                            <Form.Control size="sm" placeholder="e.g. Student" value={newLevel.id} onChange={e => setNewLevel({ ...newLevel, id: e.target.value })} className="glass-input" />
                        </Col>
                        <Col xs={6}>
                            <label className="meta-label">Cost ($)</label>
                            <Form.Control size="sm" type="number" value={newLevel.cost} onChange={e => setNewLevel({ ...newLevel, cost: e.target.value })} className="glass-input" />
                        </Col>
                        <Col xs={6}>
                            <label className="meta-label">Late Fee ($)</label>
                            <Form.Control size="sm" type="number" value={newLevel.lateFee} onChange={e => setNewLevel({ ...newLevel, lateFee: e.target.value })} className="glass-input" />
                        </Col>
                        <Col xs={12}>
                            <label className="meta-label">Occurrence</label>
                            <Form.Select size="sm" value={newLevel.occurrence} onChange={e => setNewLevel({ ...newLevel, occurrence: e.target.value })} className="glass-input">
                                <option value="Monthly">Monthly</option>
                                <option value="Annually">Annually</option>
                                <option value="One-time payment">1-Time</option>
                                <option value="Unlimited">Unlimited</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12}>
                            <label className="meta-label">Public Description</label>
                            <Form.Control size="sm" as="textarea" rows={2} value={newLevel.description} onChange={e => setNewLevel({ ...newLevel, description: e.target.value })} className="glass-input" />
                        </Col>
                        <Col xs={12} className="d-flex justify-content-between align-items-center mt-2">
                            <Form.Check type="switch" label={<span className="meta-label m-0">Show on Reg</span>} checked={newLevel.addRegistration} onChange={e => setNewLevel({ ...newLevel, addRegistration: e.target.checked })} />
                            <Form.Check type="switch" label={<span className="meta-label m-0">Account Flag</span>} checked={newLevel.account_flag} onChange={e => setNewLevel({ ...newLevel, account_flag: e.target.checked })} />
                            <Button size="sm" variant="info" className="px-3 text-white fw-bold" onClick={() => {
                                if (!newLevel.id) return toast.error("Level name required");
                                handleSave(newLevel.id, newLevel.cost, newLevel.occurrence, newLevel.addRegistration, newLevel.description, newLevel.lateFee, newLevel.account_flag);
                                setNewLevel({ id: '', cost: '', occurrence: 'Annually', addRegistration: false, account_flag: false, description: '', lateFee: '' });
                            }}>Add Level</Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <div className="d-grid gap-3">
                {Object.entries(levels).map(([id, data]) => (
                    <Card key={id} className="shadow-lg bg-black text-white">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="m-0 fw-bold text-info">{id}</h6>
                                <Button variant="link" className="text-danger p-0" onClick={() => handleDelete(id)}><FaTrashAlt /></Button>
                            </div>

                            <Row className="g-2 mb-3">
                                <Col xs={4}>
                                    <label className="meta-label">Cost</label>
                                    <Form.Control size="sm" type="number" defaultValue={data.cost} onBlur={(e) => handleSave(id, e.target.value, data.occurrence, data.addRegistration, data.description, data.lateFee, data.account_flag)} className="glass-input" />
                                </Col>
                                <Col xs={4}>
                                    <label className="meta-label">Late Fee</label>
                                    <Form.Control size="sm" type="number" defaultValue={data.lateFee} onBlur={(e) => handleSave(id, data.cost, data.occurrence, data.addRegistration, data.description, e.target.value, data.account_flag)} className="glass-input" />
                                </Col>
                                <Col xs={4}>
                                    <label className="meta-label">Type</label>
                                    <Form.Select size="sm" defaultValue={data.occurrence} onChange={(e) => handleSave(id, data.cost, e.target.value, data.addRegistration, data.description, data.lateFee, data.account_flag)} className="glass-input">
                                        <option value="Monthly">Monthly</option>
                                        <option value="Annually">Annually</option>
                                        <option value="One-time payment">1-Time</option>
                                        <option value="Unlimited">Unlimited</option>
                                    </Form.Select>
                                </Col>
                            </Row>

                            <label className="meta-label">Description</label>
                            <Form.Control size="sm" as="textarea" rows={2} defaultValue={data.description}
                                onBlur={(e) => handleSave(id, data.cost, data.occurrence, data.addRegistration, e.target.value, data.lateFee, data.account_flag)}
                                className="glass-input mb-2" style={{ fontSize: '12px' }} />

                            <div className="d-flex justify-content-between align-items-center pt-2 mb-2">
                                <span className="small opacity-75 d-flex align-items-center">
                                    {data.addRegistration ? <FaCheckCircle className="text-success me-1" /> : <FaTimesCircle className="text-danger me-1" />}
                                    <span style={{ fontSize: '10px' }} className="meta-label m-0">Registration Visible</span>
                                </span>
                                <Form.Check type="switch" checked={data.addRegistration}
                                    onChange={(e) => handleSave(id, data.cost, data.occurrence, e.target.checked, data.description, data.lateFee, data.account_flag)} />
                            </div>

                            <div className="d-flex justify-content-between align-items-center pt-2">
                                <span className="small opacity-75 d-flex align-items-center">
                                    {data.account_flag ? <FaCheckCircle className="text-info me-1" /> : <FaTimesCircle className="text-muted me-1" />}
                                    <span style={{ fontSize: '10px' }} className="meta-label m-0">Account Flag</span>
                                </span>
                                <Form.Check type="switch" checked={data.account_flag}
                                    onChange={(e) => handleSave(id, data.cost, data.occurrence, data.addRegistration, data.description, data.lateFee, e.target.checked)} />
                            </div>
                        </Card.Body>
                    </Card>
                ))}
            </div>
        </div>
    );
};

// --- HELPER FUNCTION: CANVAS CROP ---
const getCroppedImg = (imageSrc, pixelCrop) => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.src = imageSrc;
        image.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const targetSize = 300;
            canvas.width = targetSize;
            canvas.height = targetSize;

            if (!ctx) { reject(new Error("No canvas context")); return; }

            ctx.drawImage(
                image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
                0, 0, targetSize, targetSize
            );
            resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        image.onerror = (error) => reject(error);
    });
};

// --- SUB-COMPONENT: UPDATE PROFILE ---
export function UpdateProfile({ idx, filex, name, type, theme, onSendToParent, hide, isTriggerOnly = false, smallIcon }) {
    const isDark = theme === 'dark';
    const db = getFirestore();
    const fileInputRef = useRef(null);
    const [show, setShow] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [activeTab, setActiveTab] = useState('profile');
    const [historyLogs, setHistoryLogs] = useState([]);
    const [membershipRules, setMembershipRules] = useState({});

    const [cropImage, setCropImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);

    const [showQR, setShowQR] = useState(false);

    const [formData, setFormData] = useState({
        name: '', dob: '', details: '', filex: '', type: '', email: '', expirationDate: '', id: '', board: false, cell: '',
        title: '', member: false, isNew: false, otherPhone: '', address1: '', address2: '', city: '',
        state: '', zip: '', country: 'USA', company: '', jobTitle: '', paidThrough: '', serverPermit: '',
        sponsor: '', isAdmin: false, isBarAdmin: false,
    });

    const fetchMembershipRules = async () => {
        const snap = await getDocs(collection(db, "membership_levels"));
        const rules = {};
        snap.forEach(doc => { rules[doc.id] = doc.data(); });
        setMembershipRules(rules);
    };

    const fetchHistory = async (id) => {
        const historyRef = collection(db, "profiles", id, "history");
        const snap = await getDocs(historyRef);
        const logs = snap.docs.map(d => d.data());
        setHistoryLogs(logs.sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds));
    };

    const handleOpen = async () => {
        if (!idx) return;
        spinner(true);
        try {
            await fetchMembershipRules();
            const docSnap = await getDoc(doc(db, "profiles", idx));
            if (docSnap.exists()) {
                const data = docSnap.data();
                setFormData({ ...data });
                setImagePreview(data.filex || apc);
                setActiveTab('profile');
                setShow(true);
                fetchHistory(idx);
            }
        } catch (err) { toast.error("Load failed"); }
        finally { spinner(false); }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        const emailToStore = formData.email.toLowerCase().trim();

        spinner(true);
        try {
            const docRef = doc(db, "profiles", formData.id);
            const docSnap = await getDoc(docRef);
            const oldData = docSnap.data();

            if (emailToStore !== oldData.email?.toLowerCase()) {
                const q = query(collection(db, "profiles"), where("email", "==", emailToStore));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    spinner(false);
                    return toast.error("This email is already in use by another member.");
                }
            }

            const updatedData = {
                ...formData,
                isNew: formData.member ? false : formData.isNew,
                email: emailToStore,
                filex: imagePreview === apc ? '' : imagePreview
            };

            await updateDoc(docRef, updatedData);
            toast.success("Updated!");
            setShow(false);
            if (onSendToParent) onSendToParent(true);
        } catch (err) { toast.error(err.message); }
        finally { spinner(false); }
    };

    const renderFeeDisplay = () => {
        if (!formData.member || !formData.type || !membershipRules[formData.type]) return null;
        const info = membershipRules[formData.type];
        return (
            <div className={`mt-3 p-2 rounded border shadow-sm ${isDark ? 'bg-info bg-opacity-10 border-info text-info' : 'bg-info bg-opacity-10 border-info text-info'}`}>
                <div className="d-flex align-items-center justify-content-center gap-2 small">
                    <span className="fw-bold text-info">Fee</span>
                    <span className="badge bg-info text-dark ms-2">${info.cost} {info.occurrence}</span>
                </div>
            </div>
        );
    };

    const renderStatusBanner = () => {
        if (!hide) return null;
        const levelData = membershipRules[formData.type] || {};
        return (
            <div className="">
                <div className="d-flex flex-wrap justify-content-center justify-content-md-start" style={{ gap: '8px' }}>
                    {formData.board && (
                        <div className={`px-3 py-2 rounded-pill border ${isDark ? 'bg-dark border-secondary' : 'bg-white border-secondary'}`} style={{ flex: '0 1 auto' }}>
                            <label className={`meta-label d-block mb-0 opacity-50 ${!isDark ? 'text-dark' : 'text-white'}`} style={{ fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase' }}>Board</label>
                            <div className="small fw-bold text-warning d-flex align-items-center">
                                <FaCrown className="me-1" /> {formData.title}
                            </div>
                        </div>
                    )}

                    <div className={`px-3 py-2 rounded-pill border ${isDark ? 'bg-dark border-secondary' : 'bg-white border-secondary'}`} style={{ flex: '0 1 auto' }}>
                        <label className={`meta-label d-block mb-0 opacity-50 ${!isDark ? 'text-dark' : 'text-white'}`} style={{ fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase' }}>Member</label>
                        <div className={`small fw-bold ${!isDark ? 'text-dark' : 'text-white'}`}>
                            {formData.member ? formData.type : 'Non-Member'}
                        </div>
                    </div>

                    <div className={`px-3 py-2 rounded-pill border ${isDark ? 'bg-dark border-secondary' : 'bg-white border-secondary'}`} style={{ flex: '0 1 auto' }}>
                        <label className={`meta-label d-block mb-0 opacity-50 ${!isDark ? 'text-dark' : 'text-white'}`} style={{ fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase' }}>Expires</label>
                        <div className="small fw-bold text-danger d-flex align-items-center">
                            <FaCalendarAlt className="me-1" /> {formData.expirationDate ? convertDate(formData.expirationDate) : 'N/A'}
                        </div>
                    </div>

                    {levelData.account_flag && (
                        <div className={`px-3 py-2 rounded-pill border ${isDark ? 'bg-dark border-secondary' : 'bg-white border-secondary'}`} style={{ flex: '0 1 auto' }}>
                            <label className={`meta-label d-block mb-0 opacity-50 ${!isDark ? 'text-dark' : 'text-white'}`} style={{ fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase' }}>Status</label>
                            <div className="small fw-bold text-info d-flex align-items-center">
                                <FaFlag className="me-1" /> Flagged
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const modalStyles = {
        backdrop: isDark ? "adminBackdrop" : "adminBackdropWhite",
        content: isDark ? "bg-dark-profile text-white shadow-lg" : "bg-light-profile text-dark shadow-lg",
        input: isDark ? "glass-input" : "form-control border-light-subtle mb-3",
        nav: isDark ? 'profile-nav-dark' : 'profile-nav-light',
        qr: isDark ? 'bg-light-profile' : 'bg-light-profile'
    };

    return (
        <>
            {!isTriggerOnly && !hide ? (
                <Button
                    onClick={handleOpen}
                    className={
                        smallIcon
                            ? "btn-sm p-0"
                            : "circle-btn centerCircle text-danger shadow-sm"
                    }
                >
                    👤
                </Button>
            ) : (
                <Button onClick={handleOpen} >
                    <img src={filex || apc} alt="User" className="thumb hoverX animated fadeInRight mx-auto border border-3 orange rounded-circle overflow-hidden" />
                    <span className="d-block">{name || "Profile"}</span>
                </Button>
            )}

            <Modal size="sm" show={showQR} onHide={() => setShowQR(false)} contentClassName={modalStyles.qr} backdropClassName={modalStyles.backdrop} centered>
                <Modal.Header closeButton>
                    <Modal.Title><strong><span className="badge rounded-pill bg-primary"> QR CODE</span></strong></Modal.Title>
                </Modal.Header>
                <Modal.Body className="d-flex flex-column align-items-center justify-content-center text-center p-4">
                    <strong className="arrow_box_bottom">Member - <span className="orange">{formData.name}</span> <br></br>Present to staff for club access...</strong><br></br><br></br>
                    <div className="image-focus-wrapper hoverX"><br></br>
                        <QRCode value={formData.email || "none"} bgColor="white" fgColor={"#070000"} size={150} /></div><br></br><br></br>
                </Modal.Body>
            </Modal>

            <Modal show={showCropModal} onHide={() => setShowCropModal(false)} centered size="md" contentClassName={modalStyles.content} backdropClassName={modalStyles.backdrop}>
                <Modal.Header closeButton>
                    <Modal.Title>Crop Profile Photo</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ position: 'relative', width: '100%', height: '350px', background: '#222' }}>
                    {cropImage && (
                        <Cropper
                            image={cropImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            cropShape="round"
                            showGrid={false}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={(_, croppedPixels) => setCroppedAreaPixels(croppedPixels)}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex flex-column align-items-stretch gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <span className="small opacity-75">Zoom:</span>
                        <Form.Range value={zoom} min={1} max={3} step={0.1} onChange={(e) => setZoom(Number(e.target.value))} />
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-2">
                        <Button variant="secondary" size="sm" onClick={() => setShowCropModal(false)}>Cancel</Button>
                        <Button variant="info" size="sm" className="text-white fw-bold" onClick={async () => {
                            try {
                                const croppedBase64 = await getCroppedImg(cropImage, croppedAreaPixels);
                                setImagePreview(croppedBase64);
                                setShowCropModal(false);
                            } catch (err) {
                                toast.error("Failed to process image resizing.");
                            }
                        }}>Apply & Resize</Button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Modal show={show} onHide={() => setShow(false)} centered size="lg" contentClassName={modalStyles.content} backdropClassName={modalStyles.backdrop}>
                <Modal.Header className={`border-bottom border-opacity-10 px-4 ${!isDark ? 'text-dark' : ''}`}>
                    <Modal.Title className="d-flex align-items-center gap-3">
                        <div className="image-focus-wrapper hoverX">
                            <QRCode value={formData.email || "none"} size={45} onClick={() => setShowQR(true)} />
                        </div>
                        <div><h4 className="m-0">{formData.name}</h4><div className="small opacity-50">ID: {formData.id}</div></div>
                    </Modal.Title>
                    <CloseButton variant={isDark ? "white" : undefined} onClick={() => setShow(false)} />
                </Modal.Header>
                <Modal.Body className="p-0">
                    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                        <div className="px-4 pt-3 border-bottom border-opacity-10">
                            <Nav variant="pills" className={`flex-column flex-md-row gap-2 mb-3 ${modalStyles.nav}`}>
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="profile" className="text-center py-2"><FaUser className="me-2" /> General</Nav.Link></Nav.Item>
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="address" className="text-center py-2"><FaMapMarkerAlt className="me-2" /> Address</Nav.Link></Nav.Item>
                                {!hide && (
                                    <Nav.Item className="flex-fill"><Nav.Link eventKey="staff" className="text-center py-2"><FaLock className="me-2" /> Staff Only</Nav.Link></Nav.Item>
                                )}
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="history" className="text-center py-2"><FaHistory className="me-2" /> History</Nav.Link></Nav.Item>
                            </Nav>
                        </div>
                        <Tab.Content>
                            <Tab.Pane eventKey="profile">
                                <div className="p-4"><div className="row">
                                    <div className="col-md-4 text-center border-md-end border-opacity-10">
                                        <div className="mx-auto mb-3 rounded-circle overflow-hidden shadow" style={{ width: '150px', height: '150px', border: '3px solid #ff8c00' }}><img src={imagePreview || apc} alt="Avatar" className="w-100 h-100" style={{ objectFit: 'cover' }} /></div>
                                        <div className="d-grid gap-2">
                                            <Button size="sm" variant="outline-info" onClick={() => fileInputRef.current.click()}>Change Photo</Button>
                                            <ChangePasswordButton theme={theme} />
                                        </div>
                                        <input type="file" className="d-none" ref={fileInputRef} accept="image/*" onChange={(e) => {
                                            if (e.target.files && e.target.files.length > 0) {
                                                const reader = new FileReader();
                                                reader.onload = () => {
                                                    setCropImage(reader.result);
                                                    setZoom(1);
                                                    setCrop({ x: 0, y: 0 });
                                                    setShowCropModal(true);
                                                };
                                                reader.readAsDataURL(e.target.files[0]);
                                            }
                                        }} />
                                        {renderFeeDisplay()}
                                    </div>
                                    <div className="col-md-8 ps-md-4">
                                        <label className="meta-label">Full Name</label><input type="text" className={modalStyles.input} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                                        <label className="meta-label">Email Address (Primary Login ID)</label><input type="email" className={modalStyles.input} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} readOnly={hide} />
                                        <div className="row">
                                            <div className="col-6"><label className="meta-label">Cell Phone</label><input type="text" className={modalStyles.input} placeholder="123-456-7890" value={formData.cell} onChange={(e) => setFormData({ ...formData, cell: e.target.value })} /></div>
                                            <div className="col-6"><label className="meta-label">Date of Birth</label><input type="date" className={modalStyles.input} value={formData.dob} onChange={(e) => setFormData({ ...formData, dob: e.target.value })} /></div>
                                        </div>
                                        <label className="meta-label mt-2">Bio / Details</label><textarea className={modalStyles.input} rows="3" value={formData.details} onChange={(e) => setFormData({ ...formData, details: e.target.value })} />
                                    </div>
                                </div>
                                    {renderStatusBanner()}
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="address">
                                <div className="p-4">
                                    <label className="meta-label">Street Address 1</label><input type="text" className={modalStyles.input} value={formData.address1} onChange={(e) => setFormData({ ...formData, address1: e.target.value })} />
                                    <label className="meta-label">Street Address 2</label><input type="text" className={modalStyles.input} value={formData.address2} onChange={(e) => setFormData({ ...formData, address2: e.target.value })} />
                                    <div className="row g-2">
                                        <div className="col-md-4"><label className="meta-label">City</label><input type="text" className={modalStyles.input} value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} /></div>
                                        <div className="col-md-4"><label className="meta-label">State</label><input type="text" className={modalStyles.input} value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} /></div>
                                        <div className="col-md-4"><label className="meta-label">Zip</label><input type="text" className={modalStyles.input} value={formData.zip} onChange={(e) => setFormData({ ...formData, zip: e.target.value })} /></div>
                                    </div>
                                    <hr className="my-3 opacity-10" />
                                    <div className="row">
                                        <div className="col-md-6"><label className="meta-label">Company / Employer</label><input type="text" className={modalStyles.input} value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} /></div>
                                        <div className="col-md-6"><label className="meta-label">Job Title</label><input type="text" className={modalStyles.input} value={formData.jobTitle} onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })} /></div>
                                    </div>
                                </div>
                            </Tab.Pane>
                            {!hide && (
                                <Tab.Pane eventKey="staff">
                                    <div className="p-4">
                                        <div className={`p-4 rounded border ${isDark ? 'bg-secondary bg-opacity-10 border-info' : 'bg-light border-info'}`}>
                                            <div className="mb-4 border-bottom border-info border-opacity-10 pb-3">
                                                <label className="meta-label text-info d-flex align-items-center gap-2 mb-2">
                                                    <FaShieldAlt /> System Permissions
                                                </label>
                                                <Form.Check
                                                    type="switch"
                                                    id="admin-switch"
                                                    label="Admin Access (Dashboard, Directory - Master User - Unlimited Access)"
                                                    checked={formData.isAdmin}
                                                    onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
                                                    className="fw-bold text-danger"
                                                />
                                                <Form.Check
                                                    type="switch"
                                                    id="bar-admin-switch"
                                                    label="Bar Admin Access (Scanner, Incident Reporting & Manual Payment Recording)"
                                                    checked={formData.isBarAdmin}
                                                    onChange={(e) => setFormData({ ...formData, isBarAdmin: e.target.checked })}
                                                    className="fw-bold text-danger"
                                                />
                                            </div>

                                            <div className="row g-3 mb-4">
                                                <div className="col-md-6"><label className="meta-label text-info">Membership Expires</label><input type="date" className={modalStyles.input} value={formData.expirationDate} onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })} /></div>
                                                <div className="col-md-6"><label className="meta-label text-info">Paid Through</label><input type="date" className={modalStyles.input} value={formData.paidThrough} onChange={(e) => setFormData({ ...formData, paidThrough: e.target.value })} /></div>
                                            </div>
                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <Form.Check type="switch" label="Board Member" checked={formData.board} onChange={(e) => setFormData({ ...formData, board: e.target.checked })} />
                                                    {formData.board && (
                                                        <Form.Select className={modalStyles.input + " mt-2"} value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}>
                                                            <option value="">-- Choose Title --</option>
                                                            <option value="President">President</option>
                                                            <option value="Vice-President">Vice-President</option>
                                                            <option value="Secretary">Secretary</option>
                                                            <option value="Treasurer">Treasurer</option>
                                                            <option value="Director">Director</option>
                                                        </Form.Select>
                                                    )}
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="meta-label text-info">Server Permit #</label>
                                                    <input type="text" className={modalStyles.input} value={formData.serverPermit} onChange={(e) => setFormData({ ...formData, serverPermit: e.target.value })} />
                                                </div>
                                            </div>
                                            <Form.Check 
                                                type="switch" 
                                                label="Club Member" 
                                                checked={formData.member} 
                                                onChange={(e) => {
                                                    const isMemberChecked = e.target.checked;
                                                    setFormData({
                                                        ...formData,
                                                        member: isMemberChecked,
                                                        ...(isMemberChecked ? { isNew: false } : {})
                                                    });
                                                }} 
                                            />
                                            {formData.member && (
                                                <div className="mt-2 ms-4 border-start ps-3 border-info border-opacity-25">
                                                    <Form.Select className={modalStyles.input} value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                                                        <option value="">-- Type --</option>
                                                        {Object.keys(membershipRules).map(t => <option key={t} value={t}>{t}</option>)}
                                                    </Form.Select>
                                                    <label className="meta-label mt-2">Sponsor</label>
                                                    <input type="text" className={modalStyles.input} value={formData.sponsor} onChange={(e) => setFormData({ ...formData, sponsor: e.target.value })} />
                                                    {renderFeeDisplay()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Tab.Pane>
                            )}
                            <Tab.Pane eventKey="history">
                                <div className="p-4 custom-scrollbar" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                    {historyLogs.map((log, i) => (
                                        <div key={i} className={`mb-2 p-3 rounded border ${isDark ? ' bg-opacity-5' : 'bg-secondary bg-opacity-10 shadow-sm'}`}>
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span className="text-info fw-bold">{log.updatedBy}</span>
                                                <span className="opacity-50 text-end">{log.timestamp?.toDate ? convertDate(log.timestamp.toDate()) : 'Recent'}</span>
                                            </div>
                                            <div className="small opacity-75">{log.changes}</div>
                                        </div>
                                    ))}
                                </div>
                                <MemberTransactionHistory memberEmail={formData.email} />
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                    <div className="p-4 d-flex gap-2">
                        <Button variant="secondary" className="px-4" onClick={() => setShow(false)}>Cancel</Button>
                        <Button onClick={handleUpdate} variant="info" className="flex-grow-1 text-white fw-bold">Save All Changes</Button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
}

// --- SUB-COMPONENT: CREATE PROFILE ---
const CreateProfile = ({ theme, onSendToParent }) => {
    const isDark = theme === 'dark';
    const db = getFirestore();
    const [show, setShow] = useState(false);
    const [step, setStep] = useState(1);
    const [membershipRules, setMembershipRules] = useState({});

    const [formData, setFormData] = useState({
        name: '', dob: '', details: '', type: '', email: '', cell: '', otherPhone: '',
        address1: '', address2: '', city: '', state: '', zip: '', country: 'USA',
        company: '', jobTitle: '', board: false, title: '', member: false, expirationDate: '',
        paidThrough: '', serverPermit: '', sponsor: '', isAdmin: false, isBarAdmin: false
    });

    useEffect(() => {
        if (show) {
            getDocs(collection(db, "membership_levels")).then(snap => {
                const rules = {};
                snap.forEach(doc => rules[doc.id] = doc.data());
                setMembershipRules(rules);
            });
        }
    }, [show, db]);

    const handleCreate = async (e) => {
        e.preventDefault();
        const emailToStore = formData.email.toLowerCase().trim();

        spinner(true);
        try {
            const q = query(collection(db, "profiles"), where("email", "==", emailToStore));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                spinner(false);
                return toast.error("This email is already registered.");
            }

            const newId = makeid(12);
            await setDoc(doc(db, "profiles", newId), {
                ...formData,
                id: newId,
                email: emailToStore,
                isAdmin: false,
                isBarAdmin: false,
                timestamp: new Date()
            });
            toast.success("Created!");
            onSendToParent();
            setShow(false);
            setStep(1);
        } catch (err) { toast.error("Failed"); }
        finally { spinner(false); }
    };

    const modalStyles = {
        backdrop: isDark ? "bg-dark" : "bg-light",
        content: isDark ? "bg-dark text-white" : "bg-light text-dark shadow",
        input: isDark ? "glass-input" : "form-control border-light-subtle mb-3"
    };

    return (
        <>
            <button className="btn-glass-create" onClick={() => setShow(true)}>+ Add Member</button>
            <Modal show={show} onHide={() => setShow(false)} centered contentClassName={modalStyles.content}>
                <Modal.Header className={`border-bottom border-opacity-10 ${!isDark ? 'text-dark' : ''}`}>
                    <Modal.Title>New Member Profile</Modal.Title>
                    <CloseButton variant={isDark ? "white" : undefined} onClick={() => setShow(false)} />
                </Modal.Header>
                <Modal.Body className="p-4">
                    <div className="d-flex justify-content-center mb-4 gap-2">
                        {[1, 2, 3, 4].map(i => <div key={i} className={`step-dot ${step >= i ? 'active' : ''}`}></div>)}
                    </div>
                    <form onSubmit={handleCreate}>
                        {step === 1 && (
                            <div className="animated fadeIn">
                                <Form.Check type="switch" label="Board Member" checked={formData.board} onChange={(e) => setFormData({ ...formData, board: e.target.checked })} className="mb-2" />
                                {formData.board && (
                                    <Form.Select className={modalStyles.input} value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}>
                                        <option value="">-- Choose Title --</option>
                                        <option value="President">President</option>
                                        <option value="Vice-President">Vice-President</option>
                                        <option value="Secretary">Secretary</option>
                                        <option value="Treasurer">Treasurer</option>
                                        <option value="Director">Director</option>
                                    </Form.Select>
                                )}
                                <Form.Check type="switch" label="Club Member" checked={formData.member} onChange={(e) => setFormData({ ...formData, member: e.target.checked })} className="mt-2" />
                                {formData.member && (
                                    <div className="mt-2 ms-3 border-start ps-3 border-info border-opacity-25">
                                        <label className="meta-label">Membership Level</label>
                                        <Form.Select className={modalStyles.input} value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                                            <option value="">-- Choose Type --</option>
                                            {Object.keys(membershipRules).map(type => <option key={type} value={type}>{type}</option>)}
                                        </Form.Select>
                                        <div className="row g-2">
                                            <div className="col-6"><label className="meta-label">Expires</label><input type="date" className={modalStyles.input} value={formData.expirationDate} onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })} /></div>
                                            <div className="col-6"><label className="meta-label">Paid Through</label><input type="date" className={modalStyles.input} value={formData.paidThrough} onChange={(e) => setFormData({ ...formData, paidThrough: e.target.value })} /></div>
                                        </div>
                                    </div>
                                )}
                                <Button variant="info" className="w-100 text-white mt-3" onClick={() => setStep(2)}>Next</Button>
                            </div>
                        )}
                        {step === 2 && (
                            <div className="animated fadeIn">
                                <label className="meta-label">Full Name</label><input type="text" className={modalStyles.input} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                                <label className="meta-label">Email (Login ID)</label><input type="email" className={modalStyles.input} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                                <label className="meta-label">Cell Phone</label><input type="text" className={modalStyles.input} placeholder="123-456-7890" value={formData.cell} onChange={(e) => setFormData({ ...formData, cell: e.target.value })} />
                                <label className="meta-label">Date of Birth</label><input type="date" className={modalStyles.input} value={formData.dob} onChange={(e) => setFormData({ ...formData, dob: e.target.value })} />
                                <div className="d-flex gap-2 mt-3"><Button variant="secondary" className="w-50" onClick={() => setStep(1)}>Back</Button><Button variant="info" className="w-50 text-white" onClick={() => setStep(3)}>Next</Button></div>
                            </div>
                        )}
                        {step === 3 && (
                            <div className="animated fadeIn">
                                <label className="meta-label">Street Address 1</label><input type="text" className={modalStyles.input} value={formData.address1} onChange={(e) => setFormData({ ...formData, address1: e.target.value })} />
                                <label className="meta-label">Street Address 2</label><input type="text" className={modalStyles.input} value={formData.address2} onChange={(e) => setFormData({ ...formData, address2: e.target.value })} />
                                <div className="row g-2">
                                    <div className="col-md-4"><label className="meta-label">City</label><input type="text" className={modalStyles.input} value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} /></div>
                                    <div className="col-md-4"><label className="meta-label">State</label><input type="text" className={modalStyles.input} value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} /></div>
                                    <div className="col-4"><label className="meta-label">Zip</label><input type="text" className={modalStyles.input} value={formData.zip} onChange={(e) => setFormData({ ...formData, zip: e.target.value })} /></div>
                                </div>
                                <hr className="my-3 opacity-10" />
                                <label className="meta-label">Company / Employer</label><input type="text" className={modalStyles.input} value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                                <label className="meta-label">Job Title</label><input type="text" className={modalStyles.input} value={formData.jobTitle} onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })} />
                                <div className="d-flex gap-2 mt-3"><Button variant="secondary" className="w-50" onClick={() => setStep(2)}>Back</Button><Button variant="info" className="w-50 text-white" onClick={() => setStep(4)}>Next</Button></div>
                            </div>
                        )}
                        {step === 4 && (
                            <div className="text-center animated fadeIn">
                                <div className="mx-auto mb-3 rounded-circle overflow-hidden shadow" style={{ width: '120px', height: '120px', border: '2px solid #00d4ff' }}>
                                    <img src={apc} alt="Avatar Preview" className="w-100 h-100" style={{ objectFit: 'cover' }} />
                                </div>
                                <p className="small opacity-75">Click complete to finish member setup.</p>
                                <div className="d-flex gap-2"><Button variant="secondary" className="w-50" onClick={() => setStep(3)}>Back</Button><Button variant="info" className="w-50 text-white" type="submit">Complete Creation</Button></div>
                            </div>
                        )}
                    </form>
                </Modal.Body>
            </Modal>
        </>
    );
};

// --- MEMBER LIST ITEM ---
const XList = React.memo((props) => {
    const isDark = props.theme === 'dark';
    const db = getFirestore();
    const [membershipRules, setMembershipRules] = useState({});

    useEffect(() => {
        getDocs(collection(db, "membership_levels")).then(snap => {
            const rules = {};
            snap.forEach(doc => rules[doc.id] = doc.data());
            setMembershipRules(rules);
        });
    }, [db]);

    const deleteProfile = async () => {
        if (window.confirm(`Delete ${props.name}?`)) {
            spinner(true);
            try {
                await deleteDoc(doc(db, "profiles", props.id));
                toast.success("Deleted.");
                props.sendToXMembers(true);
            } catch (err) { toast.error(err.message); }
            finally { spinner(false); }
        }
    };

    const handleApprove = async () => {
        if (window.confirm(`Approval ${props.name}?`)) {
            spinner(true);
            try {
                await updateDoc(doc(db, "profiles", props.id), { member: true });
                toast.success("Approved.");
                props.sendToXMembers(true);
            } catch (err) { console.error("Error Approving:", err); }
        }
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expDate = props.expirationDate ? new Date(props.expirationDate) : null;
    let isExpired = false;
    let daysLeft = null;

    if (expDate) {
        expDate.setHours(0, 0, 0, 0);
        isExpired = expDate <= today;
        const diffTime = expDate - today;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        daysLeft = Math.ceil(diffDays);
    }

    const hasAccountFlag = membershipRules[props.type]?.account_flag;

    return (
        <div className="doc-card animated fadeIn mb-1 p-0 overflow-hidden position-relative"
            style={{ border: isDark ? '1px solid rgba(0, 212, 255, 0.15)' : '1px solid #dee2e6', background: isDark ? 'rgba(6, 6, 6, 0.03)' : '#fff' }}>

            <div className="p-1">
                <div className="d-flex justify-content-between align-items-start">
                    <div className="text-start flex-grow-1">
                        <span className="badge rounded-pill bg-primary bg-opacity-10 text-warning border px-2 ms-1" style={{ fontSize: '14px' }}>{props.id}</span>
                        <h5 className="m-0 fw-bold" style={{ color: isDark ? '#00d4ff' : '#0056b3' }}>{props.name} </h5>
                        <div className={isDark ? "text-white-50" : "text-muted"} style={{ fontSize: '11px', marginTop: '10px' }}><FaEnvelope className="me-2 text-info" /> {props.email}</div>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                            {props.board && <span className="text-warning small d-flex align-items-center"><FaCrown className="me-1" /> {props.title}</span>}
                            <span className={isDark ? "text-white-50 small" : "text-muted small"}><FaUserTag className="me-1" /> {props.type}</span>
                            {props.isAdmin && <span className="text-danger small d-flex align-items-center fw-bold"><FaShieldAlt className="me-1" /> Admin</span>}
                            {props.isBarAdmin && <span className="text-info small d-flex align-items-center fw-bold"><FaShieldAlt className="me-1" /> Bar Admin</span>}
                            {hasAccountFlag && <span className="text-info small d-flex align-items-center fw-bold"><FaFlag className="me-1" /> Flagged</span>}
                        </div>
                    </div>
                </div>
                <div className="mt-3 d-flex align-items-center justify-content-between">
                    <div>
                        {props.member ? (
                            <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border px-2 ms-1" style={{ fontSize: '10px' }}>MEMBER</span>
                        ) : (
                            <span className="badge rounded-pill bg-secondary bg-opacity-10 text-warning border px-2 ms-1" style={{ fontSize: '10px' }}>INACTIVE MEMBER</span>
                        )}

                        {Boolean(props.isNew) && (
                            <span className="badge rounded-pill bg-info bg-opacity-10 text-info border border-info px-2 ms-1" style={{ fontSize: '10px' }}>NEW REGISTRATION</span>
                        )}

                        {isExpired ? (
                            <span className="badge rounded-pill bg-danger bg-opacity-10 text-danger border px-2 ms-1" style={{ fontSize: '10px' }}>EXPIRED</span>
                        ) : (
                            <span className="badge rounded-pill bg-success bg-opacity-10 text-success border px-2 ms-1" style={{ fontSize: '10px' }}>ACTIVE</span>
                        )}

                        <span className={isDark ? "ms-2 text-white-50 small" : "ms-2 text-muted small"}>
                            <FaCalendarAlt className="me-1" size={10} />
                            {props.expirationDate ? convertDate(props.expirationDate) : 'No Date'}
                        </span>

                        {!isExpired && daysLeft !== null && daysLeft <= 40 && (
                            <span className="ms-2 text-warning fw-bold" style={{ fontSize: '11px' }}>
                                ({daysLeft} days remaining)
                            </span>
                        )}

                        {props.hasIncident && (
                            <span className="ms-2 text-warning fw-bold" style={{ fontSize: '11px' }}>
                                Has Incident
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <div className="d-flex justify-content-end border-top border-opacity-10 border-secondary p-1 m-1">
                <div className="btn-group p-1" role="group">
                    {!props.member && props.membershipStatus === 'active' ? <Button onClick={handleApprove} className="circle-btn centerCircle needs-approval" ><FaCheck className="orange" size={10} /></Button> : ''}&nbsp;
                    <UpdateProfile theme={props.theme} idx={props.id} filex={props.filex} name={props.name} onSendToParent={() => props.sendToXMembers(true)} hide={false} />&nbsp;
                    <Button onClick={deleteProfile} className="circle-btn btn-delete shadow-sm" ><FaTrashAlt size={12} /></Button>&nbsp;

                    <ManualPayment
                        profileId={props.id}
                        userEmail={props.email}
                        userName={props.name}
                        typeOfMembership={props.type}
                        expirationDate={props.expirationDate}
                        theme="dark"
                        onSendToParent={() => props.sendToXMembers(true)}
                    />
                </div>
            </div>
        </div>
    );
});

// --- SUB-COMPONENT: LAZY-LOADED INACTIVE MEMBERS OFFCANVAS ---
const InactiveMembersOffcanvas = ({ show, onHide, theme, sendToXMembers, onCountUpdate, inactiveCount, inactiveData, setInactiveData }) => {
    const isDark = theme === 'dark';
    const db = getFirestore();
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [inactiveFilter, setInactiveFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 20;

    const debouncedTerm = useDebounce(searchTerm, 200);

    // Fetch ONLY when drawer opens and local state is empty
    useEffect(() => {
        if (show && inactiveData.length === 0) {
            setLoading(true);
            const q = query(collection(db, "profiles"), where("member", "==", false));
            getDocs(q).then((snapshot) => {
                const rec = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
                setInactiveData(rec);
                if (onCountUpdate) onCountUpdate(rec.length);
            }).catch(err => {
                toast.error("Failed loading inactive members");
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [show, db, inactiveData.length, onCountUpdate, setInactiveData]);

    const auditCount = useMemo(() => {
        return inactiveData.filter(m => Boolean(m.isNew)).length;
    }, [inactiveData]);

    const filteredInactiveMembers = useMemo(() => {
        const term = debouncedTerm.toLowerCase().trim();
        let result = [...inactiveData];

        if (inactiveFilter === 'audit') {
            result = result.filter(m => Boolean(m.isNew));
        }

        if (term) {
            result = result.filter(m => {
                if (m.id?.toLowerCase().includes(term)) return true;
                if (m.name?.toLowerCase().includes(term)) return true;
                if (m.email?.toLowerCase().includes(term)) return true;
                if (m.type?.toLowerCase().includes(term)) return true;
                return false;
            });
        }

        return result;
    }, [inactiveData, debouncedTerm, inactiveFilter]);

    const paginatedMembers = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredInactiveMembers.slice(startIndex, startIndex + pageSize);
    }, [filteredInactiveMembers, currentPage]);

    const totalPages = Math.ceil(filteredInactiveMembers.length / pageSize) || 1;

    return (
        <Offcanvas
            show={show}
            onHide={onHide}
            placement="end"
            backdropClassName="adminBackdrop"
            className={isDark ? "bg-dark" : "bg-light border-start"}
        >
            <Offcanvas.Header className="pb-3 border-bottom border-opacity-10">
                <div className="w-100">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h3 className={isDark ? "modified xTitle m-0 d-flex align-items-center gap-2" : "text-dark fw-bold m-0 d-flex align-items-center gap-2"}>
                            <FaUserSlash className="text-warning" size={20} />
                            Inactive Members
                            {inactiveData.length > 0 && (
                                <Badge bg="warning" text="dark" className="rounded-pill font-11 ms-1">{inactiveData.length}</Badge>
                            )}
                        </h3>
                        <CloseButton variant={isDark ? "white" : undefined} onClick={onHide} />
                    </div>
                </div>
            </Offcanvas.Header>
            <Offcanvas.Body className="custom-scrollbar p-3">
                {/* SEARCH INPUT */}
                <div className="position-relative mb-2">
                    <Form.Control
                        type="search"
                        placeholder="🔎 Search inactive members..."
                        className={isDark ? "glass-search" : "form-control"}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                    />
                    {loading && (
                        <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                            <div className="spinner-border spinner-border-sm text-info" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* FILTER CONTROLS FOR INACTIVE MEMBERS */}
                <div className="d-flex flex-wrap gap-1 mb-3 justify-content-start align-items-center">
                    <Button 
                        size="sm" 
                        variant={inactiveFilter === 'all' ? 'info' : 'outline-secondary'} 
                        className={inactiveFilter === 'all' ? 'text-white font-11 fw-bold' : 'text-muted font-11'} 
                        onClick={() => { setInactiveFilter('all'); setCurrentPage(1); }}
                    >
                        All Inactive ({inactiveData.length})
                    </Button>
                    <Button 
                        size="sm" 
                        variant={inactiveFilter === 'audit' ? 'info' : 'outline-secondary'} 
                        className={inactiveFilter === 'audit' ? 'text-white font-11 fw-bold' : 'text-muted font-11'} 
                        onClick={() => { setInactiveFilter('audit'); setCurrentPage(1); }}
                    >
                        Audit ({auditCount})
                    </Button>
                </div>

                {/* PAGINATION CONTROLS */}
                <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                    <span className="small text-muted font-11">Page {currentPage} of {totalPages} ({filteredInactiveMembers.length} records)</span>
                    <div className="d-flex gap-1">
                        <Button 
                            size="sm" 
                            variant="info" 
                            className="rounded-pill px-3 py-1 font-11 text-white shadow-sm border-0 d-flex align-items-center gap-1" 
                            disabled={currentPage === 1 || loading} 
                            onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                        >
                            <FaArrowLeft size={9} /> Prev
                        </Button>
                        <Button 
                            size="sm" 
                            variant="info" 
                            className="rounded-pill px-3 py-1 font-11 text-white shadow-sm border-0 d-flex align-items-center gap-1" 
                            disabled={currentPage === totalPages || loading} 
                            onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                        >
                            Next <FaArrowRight size={9} />
                        </Button>
                    </div>
                </div>

                {/* PROFILES LIST */}
                <ul className="doc-container p-0">
                    {paginatedMembers.map((member) => (
                        <XList key={member.id} theme={theme} sendToXMembers={sendToXMembers} {...member} hide={false} />
                    ))}
                </ul>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

// --- MAIN EXPORT ---
function XMembers(props) {
    const { theme = 'dark' } = props;
    const isDark = theme === 'dark';
    const db = getFirestore();
    const [localShow, setLocalShow] = useState(false);
    const isCanvasOpen = props.isOpen !== undefined ? props.isOpen : localShow;
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("members");
    const [categoryFilter, setCategoryFilter] = useState("active");
    const [isSearching, setIsSearching] = useState(false);

    // Inactive drawer lazy load states
    const [showInactiveMembersDrawer, setShowInactiveMembersDrawer] = useState(false);
    const [inactiveCount, setInactiveCount] = useState(null);
    const [inactiveData, setInactiveData] = useState([]);

    const debouncedTerm = useDebounce(searchTerm, 200);

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 20;

    // Ensure Inactive Drawer automatically closes whenever the main Directory is closed
    useEffect(() => {
        if (!isCanvasOpen) {
            setShowInactiveMembersDrawer(false);
        }
    }, [isCanvasOpen]);

    // FAST INITIAL LOAD: Queries ONLY active members (member == true)
    const getMembers = useCallback(() => {
        spinner(true);
        setIsSearching(true);
        // Clear cached inactive state so it forces fresh fetch on next drawer open
        setInactiveData([]);

        const q = query(collection(db, "profiles"), where("member", "==", true));
        getDocs(q).then((snapshot) => {
            let rec = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
            setData(rec);
        }).finally(() => {
            spinner(false);
            setIsSearching(false);
        });
    }, [db]);

    const handleInitialClick = () => {
        getMembers();
        handleToggleOpen();
    };

    const handleToggleClose = () => {
        setShowInactiveMembersDrawer(false);
        if (props.onToggle) props.onToggle();
        else setLocalShow(false);
    };

    const handleToggleOpen = () => {
        if (props.onToggle) props.onToggle();
        else setLocalShow(true);
    };

    // FAST COUNTS: Calculated instantly on member-only profiles
    const counts = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let active = 0;
        let expired = 0;
        let board = 0;
        let admin = 0;
        let employees = 0;

        data.forEach(m => {
            if (m.expirationDate) {
                const expDate = new Date(m.expirationDate);
                expDate.setHours(0, 0, 0, 0);
                if (expDate > today) active++;
                else expired++;
            } else {
                expired++;
            }

            if (m.board) board++;
            if (m.isAdmin || m.isBarAdmin) admin++;
            if (m.type?.toLowerCase() === 'employee') employees++;
        });

        return { active, expired, board, admin, employees };
    }, [data]);

    // Active member filtration
    const filteredMembers = useMemo(() => {
        const term = debouncedTerm.toLowerCase().trim();
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let result = [...data];

        if (categoryFilter === 'active') {
            result = result.filter(m => {
                if (!m.expirationDate) return false;
                const expDate = new Date(m.expirationDate);
                expDate.setHours(0, 0, 0, 0);
                return expDate > today;
            });
        } else if (categoryFilter === 'expired') {
            result = result.filter(m => {
                if (!m.expirationDate) return false;
                const expDate = new Date(m.expirationDate);
                expDate.setHours(0, 0, 0, 0);
                return expDate <= today;
            });
        } else if (categoryFilter === 'board') {
            result = result.filter(m => Boolean(m.board));
        } else if (categoryFilter === 'admin') {
            result = result.filter(m => Boolean(m.isAdmin || m.isBarAdmin));
        } else if (categoryFilter === 'employees') {
            result = result.filter(m => m.type?.toLowerCase() === 'employee');
        }

        if (term) {
            result = result.filter(m => {
                if (m.id?.toLowerCase().includes(term)) return true;
                if (m.name?.toLowerCase().includes(term)) return true;
                if (m.email?.toLowerCase().includes(term)) return true;
                if (m.type?.toLowerCase().includes(term)) return true;
                if (term === 'admin' && (m.isAdmin || m.isBarAdmin)) return true;
                if (term === 'board' && m.board) return true;
                return false;
            });
        }

        return result.sort((a, b) => {
            const expA = a.expirationDate ? new Date(a.expirationDate) : null;
            const expB = b.expirationDate ? new Date(b.expirationDate) : null;
            
            if (expA) expA.setHours(0, 0, 0, 0);
            if (expB) expB.setHours(0, 0, 0, 0);

            const isExpiredA = expA ? expA <= today : true;
            const isExpiredB = expB ? expB <= today : true;

            if (!isExpiredA && isExpiredB) return -1;
            if (isExpiredA && !isExpiredB) return 1;

            return 0;
        });
    }, [data, debouncedTerm, categoryFilter]);

    const paginatedMembers = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredMembers.slice(startIndex, startIndex + pageSize);
    }, [filteredMembers, currentPage]);

    const totalPages = Math.ceil(filteredMembers.length / pageSize) || 1;

    return (
        <>
            <Button variant={isDark ? "primary" : "outline-primary"} onClick={handleInitialClick} className="hoverX shadow-sm"><span className="circle-btn centerCircle">💛</span> Directory</Button>
            
            <Offcanvas
                show={isCanvasOpen}
                onHide={handleToggleClose}
                placement="end"
                backdropClassName="adminBackdrop"
                className={isDark ? "bg-dark" : "bg-light border-start"}>
                <Offcanvas.Header className="pb-3 border-bottom border-opacity-10">
                    <div className="w-100">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h3 className={isDark ? "modified xTitle m-0" : "text-dark fw-bold m-0"}>Directory</h3>
                            <div className="d-flex align-items-center gap-2">
                                <CreateProfile theme={theme} onSendToParent={getMembers} />
                                <CloseButton variant={isDark ? "white" : undefined} onClick={handleToggleClose} />
                            </div>
                        </div>
                        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                            <Nav variant="pills" className="flex-column flex-md-row gap-2 mb-3 profile-nav-dark">
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="members" className="text-center py-2"><FaUsers className="me-2" /> Members</Nav.Link></Nav.Item>
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="manage" className="text-center py-2"><FaTools className="me-2" /> Manage Rates</Nav.Link></Nav.Item>
                            </Nav>
                        </Tab.Container>
                    </div>
                </Offcanvas.Header>
                <Offcanvas.Body className="custom-scrollbar p-0">
                    {activeTab === "members" ? (
                        <div className="p-3">
                            <div className="position-relative mb-2">
                                <Form.Control
                                    type="search"
                                    placeholder="🔎 Search members..."
                                    className={isDark ? "glass-search" : "form-control"}
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                                {isSearching && (
                                    <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                                        <div className="spinner-border spinner-border-sm text-info" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* TOP LINE FILTER BAR: Fast counters + On-Demand Inactive Member drawer */}
                            <div className="d-flex flex-wrap gap-1 mb-3 align-items-center">
                                <Button size="sm" variant={categoryFilter === 'active' ? 'info' : 'outline-secondary'} className={categoryFilter === 'active' ? 'text-white font-11 fw-bold' : 'text-muted font-11'} onClick={() => { setCategoryFilter('active'); setCurrentPage(1); }}>
                                    Active ({counts.active})
                                </Button>
                                <Button size="sm" variant={categoryFilter === 'expired' ? 'info' : 'outline-secondary'} className={categoryFilter === 'expired' ? 'text-white font-11 fw-bold' : 'text-muted font-11'} onClick={() => { setCategoryFilter('expired'); setCurrentPage(1); }}>
                                    Expired ({counts.expired})
                                </Button>
                                <Button size="sm" variant={categoryFilter === 'board' ? 'info' : 'outline-secondary'} className={categoryFilter === 'board' ? 'text-dark font-11 fw-bold' : 'text-muted font-11'} onClick={() => { setCategoryFilter('board'); setCurrentPage(1); }}>
                                    Board ({counts.board})
                                </Button>
                                <Button size="sm" variant={categoryFilter === 'admin' ? 'info' : 'outline-secondary'} className={categoryFilter === 'admin' ? 'text-white font-11 fw-bold' : 'text-muted font-11'} onClick={() => { setCategoryFilter('admin'); setCurrentPage(1); }}>
                                    Admins ({counts.admin})
                                </Button>
                                <Button size="sm" variant={categoryFilter === 'employees' ? 'info' : 'outline-secondary'} className={categoryFilter === 'employees' ? 'text-white font-11 fw-bold' : 'text-muted font-11'} onClick={() => { setCategoryFilter('employees'); setCurrentPage(1); }}>
                                    Employees ({counts.employees})
                                </Button>

                                {/* LAZY LOAD INACTIVE MEMBERS TRIGGER - INLINE AFTER EMPLOYEES */}
                                <Button size="sm" variant="outline-warning" className="font-11 d-flex align-items-center gap-1" onClick={() => setShowInactiveMembersDrawer(true)}>
                                    <FaUserSlash size={10} /> Inactive Members {inactiveCount !== null ? `(${inactiveCount})` : ''}
                                </Button>
                            </div>

                            {/* PREV & NEXT CONTROLS */}
                            <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                                <span className="small text-muted font-11">Page {currentPage} of {totalPages} ({filteredMembers.length} records)</span>
                                <div className="d-flex gap-1">
                                    <Button 
                                        size="sm" 
                                        variant="info" 
                                        className="rounded-pill px-3 py-1 font-11 text-white shadow-sm border-0 d-flex align-items-center gap-1" 
                                        disabled={currentPage === 1} 
                                        onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                                    >
                                        <FaArrowLeft size={9} /> Prev
                                    </Button>
                                    <Button 
                                        size="sm" 
                                        variant="info" 
                                        className="rounded-pill px-3 py-1 font-11 text-white shadow-sm border-0 d-flex align-items-center gap-1" 
                                        disabled={currentPage === totalPages} 
                                        onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                                    >
                                        Next <FaArrowRight size={9} />
                                    </Button>
                                </div>
                            </div>

                            <ul className="doc-container p-0">
                                {paginatedMembers.map((member) => (
                                    <XList key={member.id} theme={theme} sendToXMembers={getMembers} {...member} hide={false} />
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <MembershipManager theme={theme} />
                    )}
                </Offcanvas.Body>
            </Offcanvas>

            {/* INACTIVE MEMBERS LAZY-LOADED OFFCANVAS DRAWER PANEL */}
            <InactiveMembersOffcanvas
                show={showInactiveMembersDrawer}
                onHide={() => setShowInactiveMembersDrawer(false)}
                theme={theme}
                sendToXMembers={getMembers}
                onCountUpdate={setInactiveCount}
                inactiveCount={inactiveCount}
                inactiveData={inactiveData}
                setInactiveData={setInactiveData}
            />

            <style>{`
                .meta-label { font-size: 10px; text-transform: uppercase; opacity: 0.6; display: block; margin-bottom: 2px; }
                .font-11 { font-size: 11px; }
            `}</style>
        </>
    );
}

export default XMembers;